import { CELL_COUNT, GRID_COLS, GRID_ROWS, HeatCell, RoundMetrics, SelfReport, AttentionOracle } from "../types";
import { STIMULI } from "./stimuli";
import { clamp } from "./random";

export function blankHeat(): HeatCell[] {
  return Array.from({ length: CELL_COUNT }, (_, i) => ({ i, dwellMs: 0, touches: 0, visits: 0, longPresses: 0 }));
}

export function cellFromClientPoint(rect: DOMRect, clientX: number, clientY: number) {
  const x = clamp(clientX - rect.left, 0, rect.width - 1);
  const y = clamp(clientY - rect.top, 0, rect.height - 1);
  const col = Math.floor((x / rect.width) * GRID_COLS);
  const row = Math.floor((y / rect.height) * GRID_ROWS);
  return row * GRID_COLS + col;
}

export function summarizeHeat(heat: HeatCell[]): RoundMetrics {
  const totalDwellMs = heat.reduce((sum, cell) => sum + cell.dwellMs, 0);
  const touches = heat.reduce((sum, cell) => sum + cell.touches, 0);
  const visits = heat.reduce((sum, cell) => sum + cell.visits, 0);
  const coveredCells = heat.filter((cell) => cell.dwellMs > 120 || cell.touches > 0).length;
  const revisits = heat.filter((cell) => cell.visits > 1).length;
  const probabilities = heat
    .map((cell) => (totalDwellMs <= 0 ? 0 : cell.dwellMs / totalDwellMs))
    .filter((p) => p > 0);
  const entropyRaw = -probabilities.reduce((sum, p) => sum + p * Math.log2(p), 0);
  const entropy = probabilities.length <= 1 ? 0 : entropyRaw / Math.log2(CELL_COUNT);
  const edgeCells = heat.filter((cell) => {
    const row = Math.floor(cell.i / GRID_COLS);
    const col = cell.i % GRID_COLS;
    return row === 0 || row === GRID_ROWS - 1 || col === 0 || col === GRID_COLS - 1;
  });
  const centerCells = heat.filter((cell) => {
    const row = Math.floor(cell.i / GRID_COLS);
    const col = cell.i % GRID_COLS;
    return row >= 2 && row <= 5 && col >= 3 && col <= 8;
  });
  const edgeDwell = edgeCells.reduce((sum, cell) => sum + cell.dwellMs, 0);
  const centerDwell = centerCells.reduce((sum, cell) => sum + cell.dwellMs, 0);
  const semanticDwell = heat.reduce((sum, cell) => {
    const stimulus = STIMULI[cell.i];
    return sum + cell.dwellMs * (stimulus.family === "meaning" || stimulus.family === "social" ? 1 : 0);
  }, 0);
  return {
    coverage: coveredCells / CELL_COUNT,
    entropy,
    totalDwellMs,
    touches,
    visits,
    revisits,
    edgeBias: totalDwellMs ? edgeDwell / totalDwellMs : 0,
    centerBias: totalDwellMs ? centerDwell / totalDwellMs : 0,
    semanticBias: totalDwellMs ? semanticDwell / totalDwellMs : 0,
    skipPressure: 1 - coveredCells / CELL_COUNT,
    heat
  };
}

export function inferOracle(metrics: RoundMetrics, selfReport: SelfReport): AttentionOracle {
  const rememberedSet = new Set(selfReport.remembered);
  const highHeatRemembered = metrics.heat
    .filter((cell) => cell.dwellMs > 650 || cell.touches > 0)
    .filter((cell) => rememberedSet.has(cell.i)).length;
  const rememberedCount = Math.max(1, selfReport.remembered.length);
  const calibration = clamp(highHeatRemembered / rememberedCount, 0, 1);

  const profile =
    metrics.semanticBias > 0.34
      ? {
          name: "Semantic Magnet",
          line: "Meaning grabbed the wheel; your attention clustered around words that promised story.",
          gifts: ["fast sensemaking", "memory for narrative hooks"],
          blindspots: ["quiet geometry", "low-salience warnings"]
        }
      : metrics.edgeBias > 0.42
        ? {
            name: "Perimeter Scout",
            line: "You patrolled the borders like something important might escape through the frame.",
            gifts: ["edge monitoring", "change detection"],
            blindspots: ["central slow signals", "deep reading"]
          }
        : metrics.entropy > 0.72
          ? {
              name: "Wide Lantern",
              line: "Your gaze proxy spread broadly, trading certainty for coverage.",
              gifts: ["broad sampling", "novelty sensing"],
              blindspots: ["late-stage verification", "revisit depth"]
            }
          : metrics.revisits > 8
            ? {
                name: "Return Loop",
                line: "You came back to a few tiles repeatedly — a classic trace of inhibition-of-return losing to curiosity.",
                gifts: ["hypothesis checking", "error correction"],
                blindspots: ["fresh peripheral information", "unseen alternatives"]
              }
            : {
                name: "Needle Beam",
                line: "Your attention narrowed hard; the heatmap shows conviction more than coverage.",
                gifts: ["focus", "fast selection"],
                blindspots: ["change blindness", "soft contextual cues"]
              };

  const commonsDelta = calibration > 0.66
    ? "Your remembered map agrees with your touch/dwell trace more than most first runs."
    : "Your remembered map and inferred trace diverged — the useful little sting where self-knowledge starts.";

  return { ...profile, confidence: Math.round((0.5 + calibration * 0.45) * 100) / 100, commonsDelta };
}

export function topCells(heat: HeatCell[], count = 5) {
  return [...heat]
    .sort((a, b) => b.dwellMs + b.touches * 900 + b.visits * 250 - (a.dwellMs + a.touches * 900 + a.visits * 250))
    .slice(0, count)
    .map((cell) => cell.i);
}

export function normalizeHeatValue(cell: Pick<HeatCell, "dwellMs" | "touches" | "visits">, max = 6000) {
  return clamp((cell.dwellMs + cell.touches * 1000 + cell.visits * 220) / max, 0, 1);
}
